import { z } from 'zod'
import type { Prioridad } from '@/types/database'
import { REGLA_POR_DEFECTO } from './recurrenciaSchema'

/**
 * Opciones de prioridad para selects
 */
export const PRIORIDADES: { value: Prioridad; label: string }[] = [
  { value: 'urgente', label: 'Urgente' },
  { value: 'alta', label: 'Alta' },
  { value: 'media', label: 'Media' },
  { value: 'baja', label: 'Baja' },
]

/**
 * Schema Zod para formulario de Tarea
 */
export const tareaSchema = z
  .object({
    titulo: z.string().min(1, 'El título es requerido'),
    descripcion: z.string().nullable().optional(),
    cliente_id: z.string().nullable().optional(),
    vencimiento_id: z.string().nullable().optional(),
    asignado_a: z.string().nullable().optional(),
    prioridad: z.enum(['urgente', 'alta', 'media', 'baja']),
    fecha_limite: z.string().nullable().optional(),

    // Repetición (ORR-304). Con `repetir` en false el resto se ignora: una tarea
    // suelta se sigue creando exactamente igual que antes.
    repetir: z.boolean(),
    rec_frecuencia: z.enum(['semanal', 'mensual', 'bimestral', 'trimestral', 'semestral', 'anual']),
    rec_dia_modo: z.enum(['dia_fijo', 'primer_dia', 'ultimo_dia', 'primer_habil', 'ultimo_habil']),
    rec_dia_del_mes: z.number().int().min(1).max(31).nullable(),
    rec_dia_semana: z.number().int().min(1).max(7).nullable(),
    rec_correr_a_habil: z.boolean(),

    // Grupo de clientes: alternativa a un cliente único, con o sin repetición. Excluyente con
    // `cliente_id`.
    grupo_tipo: z.enum(['etiqueta', 'condicion_iva', 'impuesto', 'tipo_persona']).nullable(),
    grupo_valor: z.string().nullable(),
  })
  .superRefine((v, ctx) => {
    if (v.grupo_tipo) {
      // Espeja el CHECK `tareas_recurrentes_destino_unico` de la base. Sin esto, en una
      // repetición el error llega como un 400 de PostgREST que no dice qué corregir.
      if (v.cliente_id) {
        ctx.addIssue({
          code: 'custom',
          path: ['grupo_tipo'],
          message: 'Elegí un cliente o un grupo, no las dos cosas',
        })
      }
      if (!v.grupo_valor) {
        ctx.addIssue({
          code: 'custom',
          path: ['grupo_valor'],
          message: 'Elegí un valor para el grupo',
        })
      }
    }

    if (!v.repetir) return
    if (v.rec_dia_modo === 'dia_fijo' && v.rec_dia_del_mes == null) {
      ctx.addIssue({
        code: 'custom',
        path: ['rec_dia_del_mes'],
        message: 'Elegí qué día del mes se repite',
      })
    }
    if (v.rec_frecuencia === 'semanal' && v.rec_dia_semana == null) {
      ctx.addIssue({
        code: 'custom',
        path: ['rec_dia_semana'],
        message: 'Elegí qué día de la semana se repite',
      })
    }
  })

/**
 * Tipo inferido del schema
 */
export type TareaFormValues = z.infer<typeof tareaSchema>

/**
 * Valores por defecto para crear una tarea nueva
 */
export const DEFAULT_TAREA_VALUES: TareaFormValues = {
  titulo: '',
  descripcion: null,
  cliente_id: null,
  vencimiento_id: null,
  asignado_a: null,
  prioridad: 'media',
  fecha_limite: null,
  repetir: false,
  rec_frecuencia: REGLA_POR_DEFECTO.frecuencia,
  rec_dia_modo: REGLA_POR_DEFECTO.dia_modo,
  rec_dia_del_mes: REGLA_POR_DEFECTO.dia_del_mes ?? 1,
  rec_dia_semana: null,
  rec_correr_a_habil: REGLA_POR_DEFECTO.correr_a_habil,
  grupo_tipo: null,
  grupo_valor: null,
}
